/**
 * Vehicle-Product Compatibility Checking Utility
 *
 * Simplified binary compatibility checking: a product either fits or it doesn't.
 */

import { getShopData } from './shopStorage';
import { normalizeString } from './stringUtils';

/**
 * Check if stored vehicle data is complete
 * @returns {boolean} True if all required vehicle data is present
 */
export function isVehicleDataComplete(vehicleFilters) {
    if (!vehicleFilters) {
        vehicleFilters = getShopData('vehicleFilters', null);
    }

    return !!(
        vehicleFilters &&
        vehicleFilters.brand &&
        vehicleFilters.model &&
        vehicleFilters.height &&
        vehicleFilters.length &&
        vehicleFilters.constructionYear
    );
}

/**
 * Check if a product is compatible with the user's stored vehicle
 * @param {Object} product - Product object with properties array
 * @param vehicleFilters
 * @returns {Object} Compatibility result with reason
 */
export function isProductCompatibleWithStoredVehicle(product, vehicleFilters) {

    // Get vehicle data from localStorage
    if (!vehicleFilters) {
        vehicleFilters = getShopData('vehicleFilters', null);
    }

    // Check if vehicle data is completely empty or doesn't exist
    if (!vehicleFilters || (
        !vehicleFilters.brand && 
        !vehicleFilters.model && 
        !vehicleFilters.height && 
        !vehicleFilters.length && 
        !vehicleFilters.constructionYear
    )) {
        return { compatible: false, reason: 'incomplete_vehicle_data' };
    }

    // If product has no properties, assume compatible
    if (!product?.properties || !Array.isArray(product.properties)) {
        return { compatible: true, reason: 'no_product_properties' };
    }

    const vehicleData = {
        brand: vehicleFilters?.brand?.toLowerCase(),
        model: vehicleFilters?.model?.toLowerCase(),
        constructionYear: vehicleFilters?.constructionYear?.toString(),
        height: vehicleFilters?.height?.toLowerCase(),
        length: vehicleFilters?.length?.toLowerCase()
    };

    // Check if vehicle satisfies ALL property requirements
    return satisfiesAllPropertyRequirements(product.properties, vehicleData);
}

/**
 * Check if a property value indicates universal compatibility
 * @param {string} propertyName - Property name to check
 * @returns {boolean} True if property indicates universal compatibility
 */
function isUniversalProperty(propertyName) {
    const normalizedName = propertyName?.toLowerCase() || '';
    return normalizedName === 'alles' ||
           normalizedName === 'universeel' ||
           normalizedName === 'alle voertuigen';
}

/**
 * Check if vehicle satisfies ALL property requirements
 * @param {Array} properties - Product properties array
 * @param {Object} vehicleData - User's vehicle data
 * @returns {Object} Compatibility result with reason
 */
function satisfiesAllPropertyRequirements(properties, vehicleData) {
    // Group properties by type
    const vehicleProperties = [];
    const heightProperties = [];
    const lengthProperties = [];

    for (const property of properties) {
        const propertyName = property?.translated?.name || '';
        const groupName = property?.group?.translated?.name?.toLowerCase() || '';

        if (groupName === 'vehicles') {
            vehicleProperties.push(propertyName);
        } else if (groupName.includes('height')) {
            heightProperties.push(propertyName);
        } else if (groupName.includes('length')) {
            lengthProperties.push(propertyName);
        }
    }

    // Check vehicle compatibility (Brand|Model|Year pattern)
    if (vehicleProperties.length > 0) {
        const hasMatchingVehicle = vehicleProperties.some(propertyName => {
            // Check for universal vehicle compatibility
            if (isUniversalProperty(propertyName)) {
                return true;
            }

            if (propertyName.includes('|')) {
                const [brand, model, year] = propertyName.split('|').map(part => part.trim().toLowerCase());
                
                // Normalize both the property values and the vehicle data for comparison
                const normalizedBrand = normalizeString(brand);
                const normalizedModel = normalizeString(model);
                const normalizedVehicleBrand = normalizeString(vehicleData.brand);
                const normalizedVehicleModel = normalizeString(vehicleData.model);

                return normalizedBrand === normalizedVehicleBrand &&
                       normalizedModel === normalizedVehicleModel &&
                       (!year || year === vehicleData.constructionYear);
            }
            return false;
        });

        if (!hasMatchingVehicle) {
            return { compatible: false, reason: 'vehicle_mismatch' };
        }
    }

    // Check height requirements
    if (heightProperties.length > 0) {
        const hasMatchingHeight = heightProperties.some(height => {
            return isUniversalProperty(height) || height.toLowerCase() === vehicleData.height;
        });

        if (!hasMatchingHeight) {
            return { compatible: false, reason: 'height_mismatch' };
        }
    }

    // Check length requirements
    if (lengthProperties.length > 0) {
        const hasMatchingLength = lengthProperties.some(length => {
            return isUniversalProperty(length) || length.toLowerCase() === vehicleData.length;
        });

        if (!hasMatchingLength) {
            return { compatible: false, reason: 'length_mismatch' };
        }
    }

    // If we get here, all requirements are satisfied
    return { compatible: true, reason: 'compatible' };
}
